import React from 'react';
import {
  ArrowRight,
  Bot,
  CalendarDays,
  ClipboardCheck,
  Code2,
  FileSearch,
  FileText,
  ListChecks,
  MessagesSquare,
  PhoneCall,
  Search,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  CapabilityGrid,
  DEMO_PATH,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  ProblemGrid,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const AI_HANDLES = [
  { title: 'Reading resumes', detail: 'Review applications against the role before recruiter time is needed.', icon: FileSearch },
  { title: 'Applicant screening', detail: 'Bring role-relevant evidence into the hiring path.', icon: ClipboardCheck },
  { title: 'Repetitive candidate calls', detail: 'Run structured first conversations around role questions.', icon: PhoneCall },
  { title: 'Screening questions', detail: 'Collect the same early answers from every applicant.', icon: ListChecks },
  { title: 'Interview scheduling', detail: 'Help move qualified candidates to the next conversation.', icon: CalendarDays },
  { title: 'Candidate follow-up', detail: 'Keep candidates informed while the team reviews.', icon: MessagesSquare },
  { title: 'Coding review support', detail: 'Use technical assessments when the role calls for them.', icon: Code2 },
  { title: 'Candidate summaries', detail: 'Keep resume, assessment, and interview evidence together.', icon: FileText },
];

const WORKFLOW = [
  { title: 'Create the role', detail: 'Set the role and the criteria the hiring team will use.', icon: FileText },
  { title: 'Screen everyone', detail: 'Review applicants against the same role criteria.', icon: ClipboardCheck },
  { title: 'Interview automatically', detail: 'Use structured AI interviews that the team can review.', icon: Video },
  { title: 'Assess', detail: 'Use the assessment that matches the work.', icon: ListChecks },
  { title: 'Human decision', detail: 'Recruiters and hiring managers decide who moves forward.', icon: UserCheck },
  { title: 'Hire', detail: 'Your team makes the final hiring decision.', icon: Users },
];

const AI_WORK = [
  'Sourcing support',
  'Screening',
  'Repetitive interviewing',
  'Assessment',
  'Candidate evidence',
];

const HUMAN_WORK = [
  'Recruiter review',
  'Judgment',
  'Final hiring decision',
];

const CAPABILITIES = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Find relevant candidates and review resume fit against the role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: Search,
  },
  {
    title: 'AI Call Screening',
    helps: 'Run structured role-question calls before recruiter review.',
    href: '/hirewithus/ai-call-screening',
    icon: PhoneCall,
  },
  {
    title: 'Screening Assessment',
    helps: 'Score candidates against the job description before the next step.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Review role-based skills, communication, and behaviour.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Coding Assessment',
    helps: 'Add technical assessment evidence for engineering roles.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'Virtual Hiring',
    helps: 'Use structured AI interviews that your team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

export default function AiRecruitment() {
  useSEO({
    title: 'AI Recruitment Agency | SearchJob.ai',
    description:
      'AI-powered recruitment combining recruiters and AI agents for sourcing, screening, assessments, interviews and hiring.',
    canonical: 'https://searchjob.ai/solutions/ai-recruitment',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <WhatAiCanHandle />
        </ScrollReveal>
        <ScrollReveal>
          <HowItWorks />
        </ScrollReveal>
        <ScrollReveal>
          <AiAndHuman />
        </ScrollReveal>
        <ScrollReveal>
          <RelevantCapabilities />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Bot className="h-3.5 w-3.5" aria-hidden="true" />
            AI recruitment
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Your Recruitment Team. <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Powered by AI.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Hire faster without building a bigger recruitment team. SearchJob.ai combines AI hiring agents with experienced recruiters to source, screen, assess, interview and shortlist candidates, while humans stay in control of hiring decisions.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Book a Hiring Consultation
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href="#how-it-works">See How It Works</a>
            </Button>
          </div>
        </div>
        <HiringDesk />
      </div>
    </section>
  );
}

function HiringDesk() {
  const steps = [
    { label: 'AI handles the routine work', note: 'Sourcing, screening, assessments, and structured interviews', icon: Bot },
    { label: 'Recruiters review the evidence', note: 'Context and judgment stay with people', icon: UserCheck },
    { label: 'Your team makes the decision', note: 'The final hiring decision remains human', icon: Users },
  ];

  return (
    <aside className="relative self-start" aria-label="AI-supported recruitment workflow">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">One recruitment team</p>
      <ol className="space-y-3">
        {steps.map((step, index) => (
          <li key={step.label}>
            <div className="flex items-start gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <step.icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">{step.note}</span>
              </span>
            </div>
            {index < steps.length - 1 ? <span className="mx-auto my-1 block h-4 w-px bg-primary/40" aria-hidden="true" /> : null}
          </li>
        ))}
      </ol>
    </aside>
  );
}

function WhatAiCanHandle() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="What AI can handle"
        title="Give your recruiters more time for the work that needs judgment."
        copy="AI supports the repetitive parts of the hiring path. Your team stays close to candidates and in control of every decision."
      />
      <ProblemGrid items={AI_HANDLES} />
    </Section>
  );
}

function HowItWorks() {
  return (
    <Section id="how-it-works">
      <SectionIntro
        eyebrow="How it works"
        title="A complete hiring path, with people in control."
        copy="Start with the role, collect useful evidence, and let your team decide who to hire."
      />
      <IndustryWorkflow steps={WORKFLOW} />
    </Section>
  );
}

function AiAndHuman() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="AI and human control"
        title="AI handles repetitive recruitment work. Humans make hiring decisions."
        copy="SearchJob.ai helps organize the work and the evidence. It does not make the hiring decision."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <WorkList title="AI handles" items={AI_WORK} />
        <WorkList title="Your team controls" items={HUMAN_WORK} />
      </div>
    </Section>
  );
}

function WorkList({ title, items }) {
  return (
    <article className="rounded-2xl border border-primary bg-primary/5 p-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ul className="mt-4 space-y-2">
        {items.map((item) => (
          <li key={item} className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function RelevantCapabilities() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Relevant capabilities"
        title="Products already on SearchJob.ai."
        copy="Use the capabilities that fit the role and the hiring path your team needs."
      />
      <CapabilityGrid items={CAPABILITIES} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Build a recruitment team powered by AI."
      copy="Combine AI hiring support with recruiter judgment to keep your hiring process moving."
      primaryLabel="Book a Hiring Consultation"
      primaryHref={DEMO_PATH}
    />
  );
}
